/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  excludeCurrentMonth,
  formattedDate,
  getEndDateForUtility,
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import IUtilityBillComponentProps from './IUtilityBillComponentProps';
import UtilityComponentSite from '../UtilityComponent/UtilityComponentSite';
import useMounted from '../../../shared/useMount/useMount';
import { getConnctorConfig } from '../../../shared/Utils/DataConnectorUtils';
import { ISiteCard, IUtilityBillData } from '../../IEnergyManagementProps';
import moment from 'moment';
import NoData from '../../../shared/ErrorComponents/NoData';
import { EmaasSubscriptionType } from '../../../shared/Enums';

interface IUtilityBill extends ISiteCard {
  data: IUtilityBillData;
}

const UtilityBillComponentContent = (props: IUtilityBill) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return <UtilityComponentSite data={data} />;
};

const UtilityBillComponentWithLoader = withLoadingStatus(
  withErrorStatus(UtilityBillComponentContent)
);

const UtilityBillComponentSite = (props: IUtilityBillComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext } = props;
  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime =
    requestContext.context.subscriptionType ===
    EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
      ? getEndDateForUtility(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        )
      : excludeCurrentMonth(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        );

  const endTimeNewFormat = formattedDate(endTime);

  const connectorConfig = getConnctorConfig(
    'utility-bill',
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const isDateRangeValid = () => {
    const sDate = moment(
      getFirstDayofSelectedMonth(requestContext.context.startTime!),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      excludeCurrentMonth(
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const UtilityBill = (all as unknown) as IUtilityBillData;
  const isDataAvailable = UtilityBill ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [StatusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityBillData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(UtilityBill);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <UtilityBillComponentWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={StatusCode}
      onRefresh={onRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
    />
  );
};
export default UtilityBillComponentSite;
